<script setup lang="ts">
import { computed } from 'vue';
import { useMenuStore } from '@/stores/menu';
import { useI18n } from 'vue-i18n';
import type { MenuItem } from '@/types/menu';

interface Props {
  formData: {
    name: string;
    description: string;
    category_id: number | null;
    price: number;
    discount_price?: number;
    image_url: string;
    is_available: boolean;
  };
  formErrors: Record<string, string>;
  loadingCategories: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:formData': [value: any];
}>();

const menuStore = useMenuStore();
const { t } = useI18n();

const formData = computed({
  get: () => props.formData,
  set: (value) => emit('update:formData', value)
});
</script>

<template>
  <div class="space-y-4">
    <!-- Name -->
    <div>
      <label
        for="name"
        class="block text-sm font-medium text-gray-700 dark:text-white"
      >
        {{ t('app.forms.name') }} <span class="text-red-500">*</span>
      </label>
      <input
        id="name"
        :value="formData.name"
        type="text"
        class="mt-1 dark:bg-gray-900 dark:text-white block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
        :class="{ 'border-red-500': formErrors.name }"
        required
        @input="formData.name = ($event.target as HTMLInputElement).value"
      >
      <p
        v-if="formErrors.name"
        class="mt-1 text-sm text-red-600"
      >
        {{ formErrors.name }}
      </p>
    </div>

    <!-- Description -->
    <div>
      <label
        for="description"
        class="block text-sm font-medium text-gray-700 dark:text-white"
      >
        {{ t('app.forms.description') }}
      </label>
      <textarea
        id="description"
        :value="formData.description"
        rows="3"
        class="mt-1 block w-full dark:bg-gray-900 dark:text-white rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
        :class="{ 'border-red-500': formErrors.description }"
        @input="formData.description = ($event.target as HTMLTextAreaElement).value"
      />
      <p
        v-if="formErrors.description"
        class="mt-1 text-sm text-red-600"
      >
        {{ formErrors.description }}
      </p>
    </div>

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <!-- Category -->
      <div>
        <div class="flex items-center justify-between dark:text-white">
          <label
            for="category"
            class="block text-sm font-medium text-gray-700 dark:text-white"
          >
            {{ t('app.forms.category') }} <span class="text-red-500">*</span>
          </label>
        </div>
        <div class="mt-1 relative">
          <select
            id="category"
            :value="formData.category_id"
            class="block dark:bg-gray-900 dark:text-white w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
            :class="{ 
              'border-red-500': formErrors.category,
              'opacity-50': loadingCategories || menuStore.loading
            }"
            :disabled="loadingCategories || menuStore.loading"
            required
            @change="formData.category_id = Number(($event.target as HTMLSelectElement).value)"
          >
            <option
              v-if="loadingCategories || menuStore.loading"
              :value="null"
              disabled
            >
              {{ t('app.forms.loading_categories') }}
            </option>
            <option
              v-else-if="!menuStore.categoriesDetailed || menuStore.categoriesDetailed.length === 0"
              :value="null"
              disabled
            >
              {{ t('app.forms.no_categories') }}
            </option>
            <option 
              v-for="category in menuStore.categoriesDetailed"
              v-else 
              :key="category.id" 
              :value="Number(category.id)"
            >
              {{ category.name }}
            </option>
          </select>
        </div>
        <p
          v-if="formErrors.category"
          class="mt-1 text-sm text-red-600"
        >
          {{ formErrors.category }}
        </p>
      </div>

      <!-- Price -->
      <div>
        <label
          for="price"
          class="block text-sm font-medium text-gray-700 dark:text-white"
        >
          {{ t('app.forms.price_base') }} <span class="text-red-500">*</span>
        </label>
        <div class="relative mt-1 rounded-md shadow-sm">
          <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <span class="text-gray-500 sm:text-sm">{{ t('app.forms.price_symbol') }}</span>
          </div>
          <input
            id="price"
            :value="formData.price"
            type="number"
            step="0.01"
            min="0"
            class="block w-full dark:bg-gray-900 dark:text-white rounded-md border border-gray-300 pl-7 pr-12 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
            :class="{ 'border-red-500': formErrors.price }"
            :placeholder="t('app.forms.placeholder_price')"
            required
            @input="formData.price = Number(($event.target as HTMLInputElement).value)"
          >
        </div>
        <p
          v-if="formErrors.price"
          class="mt-1 text-sm text-red-600"
        >
          {{ formErrors.price }}
        </p>
      </div>
    </div>

    <!-- Discount Price (Promotional Price) -->
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div>
        <label
          for="discount_price"
          class="block text-sm font-medium text-gray-700 dark:text-white"
        >
          {{ t('app.forms.discount_price') }}
        </label>
        <div class="relative mt-1 rounded-md shadow-sm">
          <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <span class="text-gray-500 sm:text-sm">{{ t('app.forms.price_symbol') }}</span>
          </div>
          <input
            id="discount_price"
            :value="formData.discount_price"
            type="number"
            step="0.01"
            min="0"
            class="block w-full dark:bg-gray-900 dark:text-white rounded-md border border-gray-300 pl-7 pr-12 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
            :placeholder="t('app.forms.discount_placeholder')"
            @input="formData.discount_price = ($event.target as HTMLInputElement).value ? Number(($event.target as HTMLInputElement).value) : undefined"
          >
        </div>
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {{ t('app.forms.discount_price_help') }}
        </p>
      </div>
      <div
        v-if="formData.discount_price && formData.discount_price > 0"
        class="flex items-center"
      >
        <div class="rounded-md bg-green-50 dark:bg-green-900/20 p-3">
          <div class="flex">
            <div class="ml-3">
              <p class="text-sm font-medium text-green-800 dark:text-green-200">
                {{ t('app.forms.discount_active') }}
              </p>
              <p class="mt-1 text-sm text-green-700 dark:text-green-300">
                {{ t('app.forms.customers_save', { amount: `$${(formData.price - formData.discount_price).toFixed(2)}` }) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Image URL -->
    <div>
      <label
        for="image_url"
        class="block text-sm font-medium text-gray-700 dark:text-white"
      >
        {{ t('app.forms.image_url') }}
      </label>
      <input
        id="image_url"
        :value="formData.image_url"
        type="url"
        class="mt-1 block w-full dark:bg-gray-900 dark:text-white rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
        :class="{ 'border-red-500': formErrors.image_url }"
        placeholder="https://example.com/image.jpg"
        @input="formData.image_url = ($event.target as HTMLInputElement).value"
      >
      <p
        v-if="formErrors.image_url"
        class="mt-1 text-sm text-red-600"
      >
        {{ formErrors.image_url }}
      </p>
    </div>

    <!-- Availability -->
    <div class="flex items-center">
      <input
        id="is_available"
        :checked="formData.is_available"
        type="checkbox"
        class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        @change="formData.is_available = ($event.target as HTMLInputElement).checked"
      >
      <label
        for="is_available"
        class="ml-2 block text-sm text-gray-700 dark:text-white "
      >
        {{ t('app.forms.available_for_ordering') }}
      </label>
    </div>
  </div>
</template>
